// src/pages/PrintHouseDelivery/index.tsx
// Trang "Lịch sử giao vé nhà in" — README.md mục 7: liệt kê STT, Tên nhà in, Địa chỉ, Mã Kỳ, Tổng
// số vé; click "Xem chi tiết" mở modal có nút "Xem mật khẩu" (bắt buộc xác thực lại mật khẩu tài
// khoản trước khi hiện). Dữ liệu chỉ đọc — không có Redux slice riêng, cùng convention với các
// trang Reports (xem pages/Reports/IssuancePlanReport/index.tsx).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPrintHouseDeliveriesApi } from 'helpers/fakebackend_helper';
import { useBrandingTitle } from 'hooks';
import { PrintHouseDelivery, PrintHouseDeliveryPagination } from './types';
import DeliveryDetailModal from './DeliveryDetailModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('print_house_delivery');

const PAGE_SIZE = 10;

const formatNumber = (value?: number | null) => Number(value || 0).toLocaleString('vi-VN');

const PrintHouseDeliveryPage: React.FC = () => {
  useBrandingTitle(st('lich_su_giao_ve_nha_in_text'));

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deliveries, setDeliveries] = useState<PrintHouseDelivery[]>([]);
  const [pagination, setPagination] = useState<PrintHouseDeliveryPagination>({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });
  const [detailTarget, setDetailTarget] = useState<PrintHouseDelivery | null>(null);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getPrintHouseDeliveriesApi({
        Search: search || undefined,
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
      });
      const payload = res?.data || res;
      setDeliveries(payload?.items || []);
      setPagination((prev) => ({
        ...prev,
        totalItems: payload?.totalItems || 0,
        totalPages: payload?.totalPages || 0,
      }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_lich_su_giao_message'));
    } finally {
      setLoading(false);
    }
  }, [search, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadDeliveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    loadDeliveries();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('lich_su_giao_ve_nha_in_text')} pageTitle={st('ke_hoach_phat_hanh_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <h5 className="card-title mb-0">{st('lich_su_giao_ve_nha_in_text')}</h5>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSearchSubmit}>
                <Row className="g-3 mb-4 align-items-end">
                  <Col md={4}>
                    <Input
                      type="text"
                      placeholder={st('tim_theo_ten_nha_in_dia_placeholder')}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </Col>
                  <Col md="auto">
                    <Button color="primary" type="submit">
                      {st('tim_kiem_button')}
                    </Button>
                  </Col>
                </Row>
              </form>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  <DataTableShell
                    headerRow={
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                        <th>{st('ten_nha_in_text')}</th>
                        <th>{st('dia_chi_text')}</th>
                        <th>{st('ma_ky_text')}</th>
                        <th className="text-end">{st('tong_so_ve_text')}</th>
                        <th>{st('thoi_gian_text')}</th>
                        <th style={{ width: 110 }} className="text-center">
                          {st('thao_tac_column')}
                        </th>
                      </tr>
                    }
                  >
                    {deliveries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          {st('khong_co_du_lieu_text')}
                        </td>
                      </tr>
                    ) : (
                      deliveries.map((delivery, index) => (
                        <tr key={delivery.id}>
                          <td className="text-center">
                            {getSttNumber(pagination.currentPage, pagination.pageSize, index)}
                          </td>
                          <td className="fw-medium">{delivery.printHouseName}</td>
                          <td>{delivery.address}</td>
                          <td className="fw-medium text-primary">{delivery.planCode}</td>
                          <td className="text-end">{formatNumber(delivery.expectedPrintQuantity)}</td>
                          <td>{new Date(delivery.createdAt).toLocaleString('vi-VN')}</td>
                          <td className="text-center">
                            <Button
                              color="info"
                              outline
                              size="sm"
                              className="btn-icon"
                              title={st('xem_chi_tiet_title')}
                              onClick={() => setDetailTarget(delivery)}
                            >
                              <i className="ri-eye-line"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </DataTableShell>

                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <DeliveryDetailModal isOpen={!!detailTarget} delivery={detailTarget} onClose={() => setDetailTarget(null)} />
    </React.Fragment>
  );
};

export default PrintHouseDeliveryPage;
